import { useMemo } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getPaginationRowModel,
    Column,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
} from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { IanaTimezoneName, CategoryAndTag } from "api/generated/model";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { DateTimeRangeFilterValue } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeTypes";
import { dateTimeRangeTSTFilterFn } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidStringTagsFilter } from "components/table/tsTable/TSTValidStringTagsFilter";
import { TagAndCategoryListFromRecord } from "components/tag/tagAndCategory/tagAndCategoryListFromRecord";
import { buildTestingActivityViewEditUrl } from "org/controlTesting/urlUtils";
import { tagsTSTSortingFn } from "org/tags/tagsTSTUtils";
import { buildLatestEntitySnapshotOptions } from "utils/controlResults/entitySnapshotOptions";
import { useCurrentUserTimezone } from "utils/currentTimezone";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";
import { matchesCategoryAndTagFilterFn } from "utils/tags/matchesCategoryAndTagFilterFn";
import { formatTagsRecordToString } from "utils/tags/tagRecord";

import {
    DASHBOARD_PENDING_ACTIVITIES_LOCAL_STORAGE_KEY,
    DASHBOARD_PENDING_ACTIVITIES_LIST_COLUMN_IDS as COLUMN_ID,
    DASHBOARD_PENDING_ACTIVITIES_LIST_DEFAULT_STATE as DEFAULT_STATE,
    dashboardPendingActivityListColumnFiltersCodecMap,
    dashboardPendingActivityListDataParamsCodecMap,
} from "./consts";
import * as theme from "./DashboardPendingActivities.css";
import {
    filterRowsByType,
    getPendingActivityTypeFilter,
    PendingActivityTypeFilter,
} from "./pendingActivityTypeFilter";
import {
    buildRowLinkUrl,
    buildDueDateWithPrefix,
    computeAction,
    isControlActivityRow,
    isOverduePendingActivities,
    isPendingActionType,
} from "./tableUtils";
import {
    DashboardPendingActivityListColumnFilters,
    PendingActivityColumnId,
    PendingActivityTableRow,
} from "./types";

export function useDashboardPendingActivityListTable(
    pendingActivities: PendingActivityTableRow[]
): {
    table: ReturnType<typeof useReactTable<PendingActivityTableRow>>;
    getFilterComponent: (
        column: Column<PendingActivityTableRow>,
        target: FilterTarget
    ) => React.ReactNode;
    canAddException: (column: Column<PendingActivityTableRow>) => boolean;
    tableId: string;
    typeFilter: PendingActivityTypeFilter;
    clearAllFilters: () => void;
} {
    const { t } = useTranslation();
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");
    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const timezoneName = useCurrentUserTimezone();
    const notification = t("Table_RemovedFiltersWarning");
    const stateAndOnChanges = usePresetTableSearchParams(
        DASHBOARD_PENDING_ACTIVITIES_LOCAL_STORAGE_KEY,
        DEFAULT_STATE,
        dashboardPendingActivityListColumnFiltersCodecMap,
        notification,
        Sentry.captureException,
        dashboardPendingActivityListDataParamsCodecMap
    );
    const typeFilter = getPendingActivityTypeFilter(
        stateAndOnChanges.dataParams,
        controlTestingEnabled
    );
    const { includeControlActivities, includeTestingActivities } = typeFilter;
    const tableRows = useMemo(
        () =>
            filterRowsByType(pendingActivities, {
                includeControlActivities,
                includeTestingActivities,
            }),
        [pendingActivities, includeControlActivities, includeTestingActivities]
    );
    // Indexed by row index in the Due date cell, so it must cover the rows the table gets
    const isOverdueArray = useMemo(
        () => isOverduePendingActivities(tableRows),
        [tableRows]
    );
    const filteredColumns = useMemo(() => {
        const shouldShowUserPoolName = pendingActivities.some(
            (row) => isControlActivityRow(row) && row.userPoolName !== null
        );
        const shouldShowClaimantName = pendingActivities.some(
            (row) => isControlActivityRow(row) && row.claimantName !== null
        );

        return buildColumns(
            t,
            timezoneName,
            isOverdueArray,
            controlTestingEnabled
        ).filter(
            (col) =>
                (col.id !== COLUMN_ID.USER_POOL_NAME ||
                    shouldShowUserPoolName) &&
                (col.id !== COLUMN_ID.CLAIMANT_NAME ||
                    shouldShowClaimantName) &&
                (col.id !== COLUMN_ID.ENTITY || controlEntitiesEnabled) &&
                (col.id !== COLUMN_ID.ENTITIES_TO_TEST ||
                    controlTestingEnabled) &&
                (col.id !== COLUMN_ID.TEST_PROGRAM || controlTestingEnabled)
        );
    }, [
        pendingActivities,
        t,
        timezoneName,
        isOverdueArray,
        controlEntitiesEnabled,
        controlTestingEnabled,
    ]);
    const table = useReactTable({
        state: stateAndOnChanges.state,
        onStateChange: stateAndOnChanges.onStateChange,
        data: tableRows,
        columns: filteredColumns,
        initialState: {
            // useTableSearchParams doesn't support all initial states - supported states should be declared only there
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        defaultColumn: {
            size: TST_COLUMN_SIZE.M,
        },
        getCoreRowModel: getCoreRowModel<PendingActivityTableRow>(),
        getPaginationRowModel: getPaginationRowModel<PendingActivityTableRow>(),
        getSortedRowModel: getSortedRowModel<PendingActivityTableRow>(),
        getFilteredRowModel: getFilteredRowModel<PendingActivityTableRow>(),
        getFacetedUniqueValues:
            getFacetedUniqueValues<PendingActivityTableRow>(),
        getFacetedMinMaxValues:
            getFacetedMinMaxValues<PendingActivityTableRow>(),
    });

    const getFilterComponentWrapper = (
        column: Column<PendingActivityTableRow>,
        target: FilterTarget
    ): React.ReactNode =>
        getFilterComponent(t, column, target, controlTestingEnabled);

    return {
        table,
        getFilterComponent: getFilterComponentWrapper,
        canAddException: (column: Column<PendingActivityTableRow>) =>
            column.id === COLUMN_ID.TAGS ||
            column.id === COLUMN_ID.ENTITIES_TO_TEST,
        tableId: DASHBOARD_PENDING_ACTIVITIES_LOCAL_STORAGE_KEY,
        typeFilter,
        clearAllFilters: stateAndOnChanges.clearAllFilters,
    };
}

const columnHelper = createColumnHelper<PendingActivityTableRow>();

const buildColumns = (
    t: TFunction,
    timezoneName: IanaTimezoneName,
    isOverdueArray: boolean[],
    controlTestingEnabled: boolean
) => [
    columnHelper.accessor((row) => computeAction(t, row.action), {
        id: COLUMN_ID.ACTION,
        header: t("Dashboard_PendingActions_Action"),
        cell: (info) => <span>{info.getValue()}</span>,
        size: TST_COLUMN_SIZE.S,
        enableColumnFilter: true,
        enableSorting: true,
        sortingFn: "alphanumericCaseSensitive",
        filterFn: createFilterFnWithMode<PendingActivityTableRow, string[]>(
            (row, value) => value.includes(row.original.action)
        ),
    }),
    columnHelper.accessor((row) => row.activityName, {
        id: COLUMN_ID.CONTROL_TITLE,
        header: controlTestingEnabled
            ? t("Dashboard_PendingActions_Name")
            : t("Dashboard_PendingActions_Control"),
        cell: (info) => {
            const row = info.row.original;
            return isControlActivityRow(row) ? (
                <a
                    href={buildRowLinkUrl(row.id, row.action)}
                    className={theme.breakWord}
                >
                    {info.getValue()}
                </a>
            ) : (
                <Link
                    to={buildTestingActivityViewEditUrl(row.id)}
                    className={theme.breakWord}
                >
                    {info.getValue()}
                </Link>
            );
        },
        enableColumnFilter: true,
        enableSorting: true,
        enableHiding: false,
        sortingFn: "alphanumericCaseSensitive",
        filterFn: createFilterFnWithMode((row, value: string[] | null) =>
            tstArrIncludesSomeFilterFn(row, COLUMN_ID.CONTROL_TITLE, value)
        ),
    }),
    columnHelper.accessor(
        // TODO: sc-39667 Remove the id fallback
        (row) =>
            isControlActivityRow(row) && row.entity
                ? row.entity.name || String(row.entity.id)
                : "",
        {
            id: COLUMN_ID.ENTITY,
            header: t("Dashboard_PendingActions_Entity"),
            cell: (info) => (
                <span className={theme.breakWord}>{info.getValue()}</span>
            ),
            size: controlTestingEnabled ? TST_COLUMN_SIZE.S : TST_COLUMN_SIZE.M,
            enableColumnFilter: true,
            enableSorting: true,
            enableHiding: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[]) => {
                // Filter by control entity id so the deleted/renamed entities stay filterable
                const entityId = isControlActivityRow(row.original)
                    ? row.original.entity?.id
                    : undefined;
                return (
                    entityId !== undefined &&
                    value.includes(entityId.toString())
                );
            }),
        }
    ),
    columnHelper.accessor(
        (row) => formatTagsRecordToString(getEntitiesToTest(row)),
        {
            id: COLUMN_ID.ENTITIES_TO_TEST,
            header: t("Dashboard_PendingActions_EntitiesToTest"),
            cell: (info) => (
                <TagAndCategoryListFromRecord
                    tags={getEntitiesToTest(info.row.original)}
                />
            ),
            size: TST_COLUMN_SIZE.S,
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: (rowA, rowB) =>
                tagsTSTSortingFn(
                    Object.values(getEntitiesToTest(rowA.original)).flat(),
                    Object.values(getEntitiesToTest(rowB.original)).flat()
                ),
            filterFn: createFilterFnWithMode((row, value: CategoryAndTag[]) =>
                matchesCategoryAndTagFilterFn(
                    getEntitiesToTest(row.original),
                    value
                )
            ),
        }
    ),
    columnHelper.accessor(
        (row) => (isControlActivityRow(row) ? row.controlProgramName : ""),
        {
            id: COLUMN_ID.PROGRAM_NAME,
            header: t("Dashboard_PendingActions_ControlProgram"),
            cell: (info) => (
                <span className={theme.breakWord}>{info.getValue()}</span>
            ),
            size: controlTestingEnabled ? TST_COLUMN_SIZE.S : TST_COLUMN_SIZE.M,
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.PROGRAM_NAME, value)
            ),
        }
    ),
    columnHelper.accessor(
        (row) => (isControlActivityRow(row) ? "" : row.testProgramName),
        {
            id: COLUMN_ID.TEST_PROGRAM,
            header: t("Dashboard_PendingActions_TestProgram"),
            cell: (info) => (
                <span className={theme.breakWord}>{info.getValue()}</span>
            ),
            size: TST_COLUMN_SIZE.S,
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.TEST_PROGRAM, value)
            ),
        }
    ),
    columnHelper.accessor((row) => formatTagsRecordToString(row.tags), {
        id: COLUMN_ID.TAGS,
        header: t("Dashboard_PendingActions_Tags"),
        cell: (info) => (
            <TagAndCategoryListFromRecord tags={info.row.original.tags} />
        ),
        size: controlTestingEnabled ? TST_COLUMN_SIZE.S : TST_COLUMN_SIZE.M,
        enableColumnFilter: true,
        enableSorting: true,
        sortingFn: (rowA, rowB) =>
            tagsTSTSortingFn(
                Object.values(rowA.original.tags).flat(),
                Object.values(rowB.original.tags).flat()
            ),
        filterFn: createFilterFnWithMode((row, value: CategoryAndTag[]) =>
            matchesCategoryAndTagFilterFn(row.original.tags, value)
        ),
    }),
    columnHelper.accessor(
        (row) => (isControlActivityRow(row) ? (row.userPoolName ?? "") : ""),
        {
            id: COLUMN_ID.USER_POOL_NAME,
            header: t("Dashboard_PendingActions_UserPool"),
            cell: (info) => info.getValue(),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.USER_POOL_NAME, value)
            ),
        }
    ),
    columnHelper.accessor(
        (row) => (isControlActivityRow(row) ? (row.claimantName ?? "") : ""),
        {
            id: COLUMN_ID.CLAIMANT_NAME,
            header: t("Dashboard_PendingActions_Claimant"),
            cell: (info) => info.getValue(),
            enableColumnFilter: true,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, COLUMN_ID.CLAIMANT_NAME, value)
            ),
        }
    ),
    columnHelper.accessor((row) => row.pendingStepDueDate, {
        id: COLUMN_ID.DUE_DATE,
        header: t("Dashboard_PendingActions_DueDate"),
        cell: (cellContext) => {
            const dueDate = cellContext.getValue();
            const formattedDueDate = buildDueDateWithPrefix(
                t,
                dueDate,
                timezoneName
            );
            const rowIndex = cellContext.row.index;
            const isOverdue = isOverdueArray[rowIndex];
            return (
                <CellDueDate dueDate={formattedDueDate} isOverdue={isOverdue} />
            );
        },
        size: TST_COLUMN_SIZE.M,
        enableColumnFilter: true,
        enableSorting: true,
        sortingFn: "datetime",
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(value, row.original.pendingStepDueDate)
        ),
    }),
];

function getEntitiesToTest(
    row: PendingActivityTableRow
): Record<string, string[]> {
    return isControlActivityRow(row) ? {} : row.entitiesToTest;
}

function CellDueDate({
    dueDate,
    isOverdue,
}: {
    dueDate: string;
    isOverdue: boolean;
}) {
    const dotClass = isOverdue
        ? theme.cellDueDateStatusDotOverdue
        : theme.cellDueDateStatusDotPending;
    return (
        <div className={theme.cellDueDate}>
            <div className={theme.cellDueDateStatusDotContainer}>
                <div className={`${theme.cellDueDateStatusDot} ${dotClass}`} />
            </div>
            <div className={theme.cellDueDateContent}>
                <div>{dueDate}</div>
            </div>
        </div>
    );
}

function getFilterComponent(
    t: TFunction,
    column: Column<PendingActivityTableRow>,
    target: FilterTarget,
    controlTestingEnabled: boolean
): React.ReactNode {
    const getFilterHelpers =
        createFilterHelpersGetter<DashboardPendingActivityListColumnFilters>(
            column
        );
    const columnId = column.id as PendingActivityColumnId;
    switch (columnId) {
        case COLUMN_ID.ACTION: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const actions = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.action)
            );
            // A selected action that no row has still needs an option, or it shows untranslated
            for (const action of value ?? []) {
                if (isPendingActionType(action)) {
                    actions.add(action);
                }
            }
            const options = Array.from(actions).map((action) => ({
                label: computeAction(t, action),
                value: action,
            }));

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Dashboard_ActionFilter_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.CONTROL_TITLE: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((title) => ({
                label: title,
                value: title,
                customLabel: <CustomEllipsisLabel text={title} />,
            }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={
                        controlTestingEnabled
                            ? t("Dashboard_NameFilter_Placeholder")
                            : t("Dashboard_ControlFilter_Placeholder")
                    }
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.ENTITY: {
            // One option per entity id, using the latest snapshot name for display.
            // FYI: Might not be the same as the live lookup
            const options = buildLatestEntitySnapshotOptions(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original)
                    .filter(isControlActivityRow)
                    .map((row) => ({
                        original: {
                            entity: row.entity,
                            created: row.controlResultCreated,
                        },
                    }))
            ).map(({ entityId, name }) => ({
                label: name,
                value: entityId,
            }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Dashboard_EntityFilter_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.PROGRAM_NAME: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            // Test rows have no program
            const options = [...values.keys()]
                .filter((program) => program !== "")
                .map((program) => ({
                    label: program,
                    value: program,
                    customLabel: <CustomEllipsisLabel text={program} />,
                }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t(
                        "Dashboard_ControlProgramFilter_Placeholder"
                    )}
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.ENTITIES_TO_TEST: {
            const entitiesToTestPerRow = column
                .getFacetedRowModel()
                .flatRows.map((row) => getEntitiesToTest(row.original));
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );

            return (
                <TSTValidStringTagsFilter
                    placeholder={t(
                        "Dashboard_EntitiesToTestFilter_Placeholder"
                    )}
                    tags={entitiesToTestPerRow}
                    setFilterValue={setFilterValue}
                    column={column}
                    value={value}
                />
            );
        }
        case COLUMN_ID.TEST_PROGRAM: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            // Control rows have no test program
            const options = [...values.keys()]
                .filter((program) => program !== "")
                .map((program) => ({
                    label: program,
                    value: program,
                    customLabel: <CustomEllipsisLabel text={program} />,
                }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Dashboard_TestProgramFilter_Placeholder")}
                    dropdownMatchSelectWidth={500}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.TAGS: {
            const rows = column.getFacetedRowModel();
            const tagsPerRow = rows.flatRows.map((row) => row.original.tags);
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );

            return (
                <TSTValidStringTagsFilter
                    placeholder={t("Dashboard_TagFilter_Placeholder")}
                    tags={tagsPerRow}
                    setFilterValue={setFilterValue}
                    column={column}
                    value={value}
                />
            );
        }
        case COLUMN_ID.USER_POOL_NAME: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            // Test rows, and controls not assigned to a group, have no group
            const options = [...values.keys()]
                .filter((title) => title !== "")
                .map((title) => ({
                    label: title,
                    value: title,
                }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Dashboard_UserPoolFilter_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.CLAIMANT_NAME: {
            const values: Map<string, number> = column.getFacetedUniqueValues();
            // Test rows, and controls nobody has claimed, have no claimant
            const options = [...values.keys()]
                .filter((title) => title !== "")
                .map((title) => ({
                    label: title,
                    value: title,
                }));
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <TSTValidMultipleOptionFilterSelect
                    column={column}
                    options={options}
                    placeholder={t("Dashboard_ClaimantFilter_Placeholder")}
                    value={value}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case COLUMN_ID.DUE_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);

            return (
                <FilterBarDateTimeRange
                    filterValue={value}
                    setFilterValue={setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        default:
            assertNever(columnId);
    }
}
